'use client';

import { ExternalLink } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { ElasticBranchPromo } from '@//app/api/search/branch/[id]';
import { Button } from '@//components/Button';
import { Link } from '@//components/Link';
import { Modal } from '@//components/Modal';
import { PromoCard } from '@//components/PromoCard';
import { TextContentHtml } from '@//components/TextContent';
import { VStack } from '@//components/VStack';
import { urlToCfit } from '@//utils/image/urlToCfit';
import { markdownToHtml, stripMarkdown } from '@//utils/text';
import styles from './PromoItem.module.scss';

interface PromoItemProps {
  id: string;
  promo: ElasticBranchPromo;
}
const PromoItem = ({ id, promo }: PromoItemProps) => {
  const t = useTranslations('pages.item.sellerSection.promos');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const image = promo.image
    ? urlToCfit(promo.image, {
        width: 600,
        alt: promo.title,
      })
    : undefined;

  return (
    <>
      <PromoCard
        id={id}
        image={
          image
            ? {
                src: image.src,
                srcSet: image.srcSet,
                alt: image.alt,
              }
            : undefined
        }
        heading={promo.title}
        headingLevel={4}
        description={stripMarkdown(promo.description)}
        truncateDescription={true}
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsModalOpen(true)}
            title={t('modalButton.title', { name: promo.title })}
          >
            {t('modalButton.label')}
          </Button>
        }
        testId={id}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={promo.title}
        headingLevel={3}
        testId={`${id}-modal`}
      >
        <VStack gap={24}>
          {image && (
            <img
              src={image.src}
              srcSet={image.srcSet}
              alt={image.alt}
              className={styles.modalImage}
            />
          )}
          <TextContentHtml textSize="md" html={markdownToHtml(promo.description)} />
          <Link
            href={promo.link.url}
            title={t('readMoreLink.title', { name: promo.title })}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {promo.link.name}
            <ExternalLink aria-hidden="true" />
          </Link>
        </VStack>
      </Modal>
    </>
  );
};

export default PromoItem;
